import dayjs from 'utils/dayjs';

import { SORT_ORDER } from 'constants/utils';
import {
  CURRENCY_YEAR_ARCHIVE_PRIMARY_CURRENCIES,
  CURRENCY_YEAR_ARCHIVE_SOURCE,
} from 'constants/currencyYearArchive';
import { parseDate } from './date';
import { DEFAULT_SOURCE_CURRENCY } from './defaultValues';
import { getLocaleUrlPath } from './i18n';

export const EMPTY_CURRENCY_YEAR_ARCHIVE_SOURCE = {
  hasData: false,
  months: [],
  yearEnd: null,
};

const CURRENCY_YEAR_ARCHIVE_MONTH_KEYS = Array.from(
  { length: 12 },
  (_, index) => String(index + 1).padStart(2, '0')
);

const mapCurrencyYearArchiveDay = day => {
  if (!day?.date) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(day, 'rate')) {
    return {
      date: day.date,
      rate: day.rate != null ? day.rate : null,
    };
  }

  return {
    date: day.date,
    buy: day.buy != null ? day.buy : null,
    sell: day.sell != null ? day.sell : null,
  };
};

const mapCurrencyYearArchiveMonth = (monthKey, monthData) => {
  if (!monthData || typeof monthData !== 'object') {
    return null;
  }

  const days = (Array.isArray(monthData.days) ? monthData.days : [])
    .map(mapCurrencyYearArchiveDay)
    .filter(Boolean);

  if (!days.length) {
    return null;
  }

  const month = Number.parseInt(monthKey, 10);
  const median = monthData.median || {};

  if (Object.prototype.hasOwnProperty.call(median, 'rate')) {
    return {
      month,
      averageRate: median.rate != null ? median.rate : null,
      days,
    };
  }

  return {
    month,
    averageBuy: median.buy != null ? median.buy : null,
    averageSell: median.sell != null ? median.sell : null,
    days,
  };
};

const getCurrencyYearArchiveYearEnd = months => {
  const lastMonth = months[months.length - 1];
  const lastDay = lastMonth?.days?.[lastMonth.days.length - 1];

  if (!lastDay) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(lastDay, 'rate')) {
    return {
      rate: lastDay.rate,
      asOfDate: lastDay.date,
    };
  }

  return {
    buy: lastDay.buy,
    sell: lastDay.sell,
    asOfDate: lastDay.date,
  };
};

export const mapCurrencyYearArchiveSource = apiResponse => {
  if (!apiResponse || apiResponse.success === false) {
    return EMPTY_CURRENCY_YEAR_ARCHIVE_SOURCE;
  }

  const apiMonths = apiResponse.months;

  if (!apiMonths || typeof apiMonths !== 'object') {
    return EMPTY_CURRENCY_YEAR_ARCHIVE_SOURCE;
  }

  const months = CURRENCY_YEAR_ARCHIVE_MONTH_KEYS.map(monthKey =>
    mapCurrencyYearArchiveMonth(monthKey, apiMonths[monthKey])
  ).filter(Boolean);

  if (!months.length) {
    return EMPTY_CURRENCY_YEAR_ARCHIVE_SOURCE;
  }

  return {
    hasData: true,
    months,
    yearEnd: getCurrencyYearArchiveYearEnd(months),
  };
};

export const getCurrencyYearArchiveSourceSectionId = source =>
  `currency-year-archive-source-${source}`;

const CURRENCY_YEAR_ARCHIVE_SCROLL_OFFSET = 64;

export const scrollToCurrencyYearArchiveSourceSection = source => {
  const scroll = () => {
    const element = document.getElementById(
      getCurrencyYearArchiveSourceSectionId(source)
    );

    if (!element) {
      return false;
    }

    const top =
      window.scrollY +
      element.getBoundingClientRect().top -
      CURRENCY_YEAR_ARCHIVE_SCROLL_OFFSET;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: 'smooth',
    });

    return true;
  };

  if (scroll()) {
    return undefined;
  }

  let attempts = 0;
  let timeoutId;

  const retry = () => {
    if (scroll() || attempts >= 10) {
      return;
    }

    attempts += 1;
    timeoutId = window.setTimeout(retry, 50);
  };

  timeoutId = window.setTimeout(retry, 0);

  return () => window.clearTimeout(timeoutId);
};

export const getCurrencyYearArchiveMinYear = () => {
  const cutoverDate = getCurrencyArchiveCutoverDate();

  if (cutoverDate) {
    return cutoverDate.year();
  }

  return new Date().getFullYear();
};

export const getCurrencyYearArchiveAvailableYears = () => {
  const minYear = getCurrencyYearArchiveMinYear();
  const maxYear = new Date().getFullYear();
  const years = [];

  for (let year = minYear; year <= maxYear; year += 1) {
    years.push(year);
  }

  return years;
};

export const buildCurrencyYearArchiveFromApiResponses = (
  currency,
  year,
  responsesBySource
) => {
  const sources = Object.values(CURRENCY_YEAR_ARCHIVE_SOURCE).reduce(
    (acc, sourceKey) => {
      acc[sourceKey] = mapCurrencyYearArchiveSource(
        responsesBySource[sourceKey]
      );
      return acc;
    },
    {}
  );

  return {
    currency: currency.toLowerCase(),
    year,
    availableYears: getCurrencyYearArchiveAvailableYears(),
    availableCurrencies: CURRENCY_YEAR_ARCHIVE_PRIMARY_CURRENCIES,
    sources,
  };
};

const parseCurrencyArchiveDay = value => {
  const parsed = parseDate(value);

  return parsed ? parsed.startOf('day') : null;
};

export const getCurrencyArchiveCutoverDate = () =>
  parseCurrencyArchiveDay(process.env.APP_CURRENCY_ARCHIVE_CUTOVER_AT);

export const shouldNavigateToCurrencyYearArchive = selectedDate => {
  const cutoverDate = getCurrencyArchiveCutoverDate();
  const selectedDay = parseCurrencyArchiveDay(selectedDate);

  if (!cutoverDate || !selectedDay) {
    return false;
  }

  return selectedDay.isAfter(cutoverDate);
};

export const getCurrencyYearArchivePath = (currency, year) => {
  const normalizedCurrency = currency?.toLowerCase();
  const normalizedYear = year != null ? String(year) : '';

  if (!normalizedCurrency || !/^\d{4}$/.test(normalizedYear)) {
    return null;
  }

  return `/archive/${normalizedCurrency}/${normalizedYear}/`;
};

export const getCurrencyYearArchiveHref = ({
  currency,
  year,
  locale,
  highlightDate,
}) => {
  const path = getCurrencyYearArchivePath(currency, year);

  if (!path) {
    return null;
  }

  const href = `${getLocaleUrlPath(locale)}${
    process.env.APP_ROUTER_BASENAME
  }${path}`;

  if (!highlightDate) {
    return href;
  }

  return `${href}#${highlightDate}`;
};

export const prefetchCurrencyYearArchive = ({
  selectedDate,
  currency,
  locale,
}) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (!shouldNavigateToCurrencyYearArchive(selectedDate)) {
    return;
  }

  const selectedDay = parseCurrencyArchiveDay(selectedDate);
  const href = selectedDay
    ? getCurrencyYearArchiveHref({
        currency: currency || DEFAULT_SOURCE_CURRENCY,
        year: selectedDay.year(),
        locale,
      })
    : null;

  if (!href) {
    return;
  }

  const prefetchSelector = `link[rel="prefetch"][href="${href}"]`;

  if (document.head.querySelector(prefetchSelector)) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  document.head.appendChild(link);
};

export const navigateCurrencyDatePicker = ({
  history,
  selectedDate,
  currency,
  legacyPath,
  locale,
  method = 'push',
}) => {
  if (!shouldNavigateToCurrencyYearArchive(selectedDate)) {
    if (legacyPath) {
      history[method](legacyPath);
    }

    return;
  }

  const selectedDay = parseCurrencyArchiveDay(selectedDate);
  const href = selectedDay
    ? getCurrencyYearArchiveHref({
        currency: currency || DEFAULT_SOURCE_CURRENCY,
        year: selectedDay.year(),
        locale,
        highlightDate: selectedDay.format('YYYY-MM-DD'),
      })
    : null;

  if (!href) {
    if (legacyPath) {
      history[method](legacyPath);
    }

    return;
  }

  window.location.assign(href);
};

export const isFutureArchiveYear = year => {
  const parsedYear = Number.parseInt(String(year), 10);

  return !Number.isInteger(parsedYear) || parsedYear > new Date().getFullYear();
};

export const isCurrencyYearArchiveForRoute = (archive, currency, year) => {
  if (!archive) {
    return false;
  }

  if (archive.currency?.toLowerCase() !== currency?.toLowerCase()) {
    return false;
  }

  return String(archive.year) === String(year);
};

export const hasCurrencyYearArchiveForRequest = (currency, year, archive) => {
  if (isFutureArchiveYear(year)) {
    return false;
  }

  return isCurrencyYearArchiveForRoute(archive, currency, year);
};

export const parseCurrencyYearArchiveHighlightDate = (hash, year) => {
  const raw = hash?.replace(/^#/, '');

  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null;
  }

  if (raw.slice(0, 4) !== String(year)) {
    return null;
  }

  return raw;
};

export const formatCurrencyYearArchiveChartMonthLabel = (isoDate, locale) => {
  const parsed = dayjs(isoDate, 'YYYY-MM-DD', true).locale(locale);

  if (!parsed.isValid()) {
    return '';
  }

  const monthLabel = parsed.format('MMM');
  const withPeriod = monthLabel.endsWith('.') ? monthLabel : `${monthLabel}.`;

  return withPeriod.charAt(0).toUpperCase() + withPeriod.slice(1);
};

export const formatCurrencyYearArchiveChartTooltipDate = (isoDate, locale) => {
  const parsed = dayjs(isoDate, 'YYYY-MM-DD', true).locale(locale);

  if (!parsed.isValid()) {
    return '';
  }

  return parsed.format('DD MMMM YYYY');
};

export const getCurrencyYearArchiveDayByDate = (source, isoDate) => {
  if (!source?.months?.length || !isoDate) {
    return null;
  }

  for (let index = 0; index < source.months.length; index += 1) {
    const month = source.months[index];
    const day = month.days?.find(item => item.date === isoDate);

    if (day) {
      return day;
    }
  }

  return null;
};

export const isCurrentArchiveYear = year =>
  Number.parseInt(String(year), 10) === new Date().getFullYear();

export const isCurrentArchiveMonth = (month, year) => {
  if (!isCurrentArchiveYear(year)) {
    return false;
  }

  const parsedMonth = Number.parseInt(String(month), 10);

  if (!Number.isInteger(parsedMonth) || parsedMonth < 1 || parsedMonth > 12) {
    return false;
  }

  return parsedMonth === new Date().getMonth() + 1;
};

export const getCurrencyYearArchiveDefaultDate = ({
  year,
  months,
  highlightedDate,
}) => {
  const parsedYear = Number.parseInt(String(year), 10);
  const today = dayjs().startOf('day');
  const archiveDays = (months || []).reduce((days, month) => {
    (month?.days || []).forEach(day => {
      if (!day?.date) {
        return;
      }

      const parsed = dayjs(String(day.date).slice(0, 10), 'YYYY-MM-DD', true);

      if (parsed.isValid()) {
        days.push(parsed);
      }
    });

    return days;
  }, []);

  const getLatestDay = days => {
    if (!days.length) {
      return null;
    }

    return days.reduce((latest, day) => (day.isAfter(latest) ? day : latest));
  };

  if (highlightedDate) {
    const highlighted = dayjs(highlightedDate, 'YYYY-MM-DD', true);

    if (highlighted.isValid()) {
      const highlightedKey = highlighted.format('YYYY-MM-DD');
      const hasExactDay = archiveDays.some(
        day => day.format('YYYY-MM-DD') === highlightedKey
      );

      if (hasExactDay) {
        return highlighted;
      }

      const latestOnOrBeforeHighlight = getLatestDay(
        archiveDays.filter(day => !day.isAfter(highlighted, 'day'))
      );

      if (latestOnOrBeforeHighlight) {
        return latestOnOrBeforeHighlight;
      }
    }
  }

  if (Number.isInteger(parsedYear) && parsedYear === today.year()) {
    const todayKey = today.format('YYYY-MM-DD');
    const hasToday = archiveDays.some(
      day => day.format('YYYY-MM-DD') === todayKey
    );

    if (hasToday) {
      return today.clone();
    }

    const latestAvailableTodayOrPast = getLatestDay(
      archiveDays.filter(day => !day.isAfter(today))
    );

    if (latestAvailableTodayOrPast) {
      return latestAvailableTodayOrPast;
    }

    return today.clone();
  }

  const latestInYear = getLatestDay(archiveDays);

  if (latestInYear) {
    return latestInYear;
  }

  if (Number.isInteger(parsedYear)) {
    if (parsedYear < today.year()) {
      return dayjs(`${parsedYear}-12-31`, 'YYYY-MM-DD', true);
    }

    return dayjs(`${parsedYear}-01-01`, 'YYYY-MM-DD', true);
  }

  return null;
};

export const getCurrencyYearArchiveDefaultExpandedMonth = ({
  year,
  months,
  highlightedDate,
}) => {
  if (!months?.length) {
    return null;
  }

  const monthsWithData = months.filter(
    month => Array.isArray(month?.days) && month.days.length > 0
  );

  if (!monthsWithData.length) {
    return null;
  }

  const findNearestPreviousMonth = targetMonth => {
    const previousMonths = monthsWithData
      .filter(month => Number(month.month) <= targetMonth)
      .sort((left, right) => Number(right.month) - Number(left.month));

    if (previousMonths.length) {
      return Number(previousMonths[0].month);
    }

    const latestMonth = monthsWithData.reduce((latest, month) =>
      Number(month.month) > Number(latest.month) ? month : latest
    );

    return Number(latestMonth.month);
  };

  if (highlightedDate) {
    const highlighted = dayjs(highlightedDate, 'YYYY-MM-DD', true);

    if (highlighted.isValid()) {
      return findNearestPreviousMonth(highlighted.month() + 1);
    }
  }

  const defaultDate = getCurrencyYearArchiveDefaultDate({
    year,
    months,
    highlightedDate: null,
  });

  if (!defaultDate?.isValid()) {
    return null;
  }

  return findNearestPreviousMonth(defaultDate.month() + 1);
};

export const getCurrencyYearArchiveUpdatedDate = archive =>
  archive?.sources?.cash?.yearEnd?.asOfDate || null;

export const getCurrencyYearArchiveDateOptions = archive => {
  const months = archive?.sources?.cash?.months;

  if (!months?.length) {
    return [];
  }

  return months.reduce((dates, month) => {
    (month.days || []).forEach(day => {
      if (day?.date) {
        dates.push(day.date);
      }
    });

    return dates;
  }, []);
};

export const sortCurrencyYearArchiveDays = (days, sort) => {
  if (!days?.length || !sort?.key) {
    return days || [];
  }

  const { key, order } = sort;
  const sorted = [...days];

  sorted.sort((left, right) => {
    const leftValue = left[key];
    const rightValue = right[key];

    if (leftValue == null && rightValue == null) {
      return 0;
    }

    if (leftValue == null) {
      return 1;
    }

    if (rightValue == null) {
      return -1;
    }

    let comparison = 0;

    if (leftValue < rightValue) {
      comparison = -1;
    } else if (leftValue > rightValue) {
      comparison = 1;
    }

    return order === SORT_ORDER.desc ? -comparison : comparison;
  });

  return sorted;
};

export const getNextCurrencyYearArchiveSort = (currentSort, columnKey) => {
  if (currentSort?.key === columnKey) {
    return {
      key: columnKey,
      order:
        currentSort.order === SORT_ORDER.asc ? SORT_ORDER.desc : SORT_ORDER.asc,
    };
  }

  return {
    key: columnKey,
    order: SORT_ORDER.asc,
  };
};

const EMPTY_RATE = '—';

export const formatCurrencyYearArchiveCashRate = (value, formatNumber) => {
  if (value == null || Number.isNaN(Number(value))) {
    return EMPTY_RATE;
  }

  return formatNumber(Number(value), {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

export const formatCurrencyYearArchiveNbuRate = (value, formatNumber) => {
  if (value == null || Number.isNaN(Number(value))) {
    return EMPTY_RATE;
  }

  return formatNumber(Number(value), {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

export const splitCurrencyYearArchiveNbuRate = (value, formatNumber) => {
  const formatted = formatCurrencyYearArchiveNbuRate(value, formatNumber);

  if (formatted === EMPTY_RATE) {
    return null;
  }

  const commaIndex = formatted.indexOf(',');

  if (commaIndex === -1) {
    return { main: formatted, suffix: '' };
  }

  return {
    main: formatted.slice(0, commaIndex + 3),
    suffix: formatted.slice(commaIndex + 3),
  };
};

export { EMPTY_RATE };
